/**
 * useSmswiserDrops
 * For Media Devoted | Created By Ciro Autiero
 *
 * Fetches SMSWiser drops through the backend proxy.
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  buildDropsApiParams,
  getDropsRequestTimeoutMs,
} from '@/features/drops/utils/buildDropsApiParams';
import api from '@/services/api';
import { unwrapApiData } from '@/hooks/smswiser-campaign-workspace/utils';
import type { SmswiserDrop, SmswiserDropsAllResponse } from '@/types/smswiser-drops';

function extractErrorMessage(error: unknown): string {
  const maybe = error as {
    message?: string;
    response?: {
      data?: {
        message?: string;
        error?: string | { code?: string; message?: string };
      };
    };
  };

  const data = maybe?.response?.data;
  if (data?.message && typeof data.message === 'string') return data.message;
  const err = data?.error;
  if (typeof err === 'object' && err !== null && typeof (err as { message?: string }).message === 'string') {
    return (err as { message: string }).message;
  }
  if (typeof err === 'string') return err;
  if (typeof maybe?.message === 'string') return maybe.message;
  return 'Request failed';
}

export interface SmswiserDropsQueryParams {
  search: string;
  /** Country land code (e.g. "AU") — uses SMSWiser's native `landCode` filter to
   *  scope drops to one country. The correct way to list a country's drops. */
  landCode?: string;
  start: number;
  length: number;
  /** Optional ISO date — when set, backend paginates SMSWiser until oldest drop precedes `from`. */
  from?: string;
  /** Optional ISO date — paired with `from` for date-range fetch sizing. */
  to?: string;
}

export const smswiserDropsKeys = {
  all: ['smswiser-drops'] as const,
  list: (params: SmswiserDropsQueryParams) =>
    [...smswiserDropsKeys.all, 'list', params] as const,
  byUniqueId: (uniqueId: string) =>
    [...smswiserDropsKeys.all, 'by-unique-id', uniqueId] as const,
};

export function useSmswiserDrops(params: SmswiserDropsQueryParams) {
  return useQuery({
    queryKey: smswiserDropsKeys.list(params),
    queryFn: async () => {
      try {
        const apiParams = buildDropsApiParams(params);
        const timeout = getDropsRequestTimeoutMs(params);
        const { data } = await api.get<{ success: boolean; data: SmswiserDropsAllResponse }>(
          '/smswiser/drops/all',
          { params: apiParams, ...(timeout ? { timeout } : {}) },
        );
        return data.data;
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    staleTime: 60_000,
  });
}

export function useSmswiserDropByUniqueId(uniqueId: string) {
  return useQuery({
    queryKey: smswiserDropsKeys.byUniqueId(uniqueId),
    queryFn: async () => {
      try {
        const { data } = await api.get<{ success: boolean; data: SmswiserDrop }>(
          '/smswiser/drops/by-unique-id',
          { params: { uniqueId } },
        );
        return data.data;
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    enabled: !!uniqueId,
    staleTime: 10_000,
  });
}

export function useKillSmswiserDrop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { uniqueId?: string; dropId?: number }) => {
      try {
        const { data } = await api.post<{ success: boolean; message?: string }>(
          '/smswiser/drops/kill',
          params,
        );
        return data;
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: smswiserDropsKeys.all });
    },
  });
}

export interface KillBatchResult {
  uniqueId?: string;
  dropId?: number;
  success: boolean;
  error?: string;
}

/** Bulk kill — stops many drops in concurrent server-side batches (idempotent). */
export function useKillSmswiserDropBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      drops: Array<{
        uniqueId?: string;
        dropId?: number;
        preparedDropId?: string;
      }>,
    ) => {
      try {
        // The API wraps responses in { success, data, meta }; unwrap so callers
        // get { success, message, results } directly (else results is undefined).
        const res = await api.post('/smswiser/drops/kill-batch', { drops });
        return unwrapApiData<{
          success: boolean;
          message?: string;
          results: KillBatchResult[];
        }>(res.data);
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: smswiserDropsKeys.all });
    },
  });
}

export interface EditSmswiserDropParams {
  dropId: number;
  /** SMSWiser drop uniqueId — required by the API edit (PATCH /drops/{uniqueId}). */
  uniqueId?: string;
  title?: string;
  senderName?: string;
  smsText?: string[];
  campaign?: string;
  smsGatewayRoute?: string;
  shortLinkTld?: string;
  forceSend?: boolean;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
}

export function useEditSmswiserDrop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: EditSmswiserDropParams) => {
      try {
        const { data } = await api.post<{ success: boolean; message?: string }>(
          '/smswiser/drops/edit',
          params,
        );
        return data;
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: smswiserDropsKeys.all });
    },
  });
}

export function useEditSmswiserDropBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payloads: EditSmswiserDropParams[]) => {
      try {
        const res = await api.post('/smswiser/drops/edit-batch', { payloads });
        // Unwrap the { success, data, meta } envelope so `results` is accessible.
        return unwrapApiData<{
          success: boolean;
          message?: string;
          results: Array<{ dropId: number; success: boolean; error?: string }>;
        }>(res.data);
      } catch (error) {
        throw new Error(extractErrorMessage(error));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: smswiserDropsKeys.all });
    },
  });
}
